export interface CsvEmailRecipient {
  email: string;
  ceaNumber?: string;
  /**
   * Ordered variable values parsed from columns named `var1`, `var2`, …
   * Index 0 corresponds to `{{1}}` in the template body / subject.
   */
  variables?: string[];
  [key: string]: string | string[] | undefined;
}

const CEA_HEADER_KEYS = ['ceanumber', 'cea', 'cea_number', 'cea number'];
const EMAIL_HEADER_KEYS = ['email', 'emailaddress', 'email_address', 'e-mail'];

// Minimal sanity check — backend should still validate properly.
const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Parse a CSV of email recipients. The first column is treated as the email
 * address when no `email` header is present. Supports a header row with the
 * columns `email`, `CEANumber`, and `var1`, `var2`, … in any order.
 */
export function parseCsvEmailRecipients(text: string): CsvEmailRecipient[] {
  const rows = parseCsvRows(text);
  if (rows.length === 0) return [];

  let headers: string[] | null = null;
  const first = rows[0];
  const firstLooksLikeHeader = first.some(
    (cell) => /[a-zA-Z]/.test(cell) && !EMAIL_LIKE.test(cell.trim()),
  );
  let startIndex = 0;
  if (firstLooksLikeHeader) {
    headers = first.map((h) => h.trim().toLowerCase());
    startIndex = 1;
  }

  const recipients: CsvEmailRecipient[] = [];
  for (let r = startIndex; r < rows.length; r += 1) {
    const row = rows[r];
    if (row.every((cell) => cell.trim() === '')) continue;

    const recipient: CsvEmailRecipient = { email: '' };
    if (headers) {
      const stringByKey: Record<string, string> = {};
      headers.forEach((key, idx) => {
        const value = (row[idx] || '').trim();
        stringByKey[key] = value;
        recipient[key] = value;
      });
      const emailKey = headers.find((h) => EMAIL_HEADER_KEYS.includes(h)) || headers[0];
      recipient.email = (stringByKey[emailKey] || row[0] || '').trim();
      const ceaKey = headers.find((h) => CEA_HEADER_KEYS.includes(h));
      if (ceaKey) recipient.ceaNumber = (stringByKey[ceaKey] || '').trim();

      const varEntries: Array<{ index: number; value: string }> = [];
      headers.forEach((key) => {
        const match = /^var(\d+)$/.exec(key);
        if (match) {
          const idx = Number(match[1]);
          if (Number.isFinite(idx) && idx >= 1) {
            varEntries.push({ index: idx, value: stringByKey[key] ?? '' });
          }
        }
      });
      if (varEntries.length > 0) {
        const maxIndex = varEntries.reduce((m, e) => (e.index > m ? e.index : m), 0);
        const variables = new Array<string>(maxIndex).fill('');
        for (const entry of varEntries) variables[entry.index - 1] = entry.value;
        recipient.variables = variables;
      }
    } else {
      // No header row: assume column order [email, ceaNumber?].
      recipient.email = (row[0] || '').trim();
      if (row[1]) recipient.ceaNumber = row[1].trim();
    }
    if (recipient.email) recipients.push(recipient);
  }

  return recipients;
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let current: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      continue;
    }
    if (char === ',') {
      current.push(cell);
      cell = '';
      continue;
    }
    if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      current.push(cell);
      rows.push(current);
      current = [];
      cell = '';
      continue;
    }
    cell += char;
  }

  if (cell.length > 0 || current.length > 0) {
    current.push(cell);
    rows.push(current);
  }
  return rows;
}

export function isLikelyValidEmail(value: string): boolean {
  return EMAIL_LIKE.test(value.trim());
}
